import { practiceApi } from '../../services/authApi';

export const fmtDateUS = (d) => (d ? new Date(d).toLocaleDateString('en-US') : '—');
export const safeText = (s) => (s == null ? '—' : String(s).replace(/[<>&]/g, (m) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[m])));

export const printProcedure = async (procedureId, { dashboardData, practice, toast }) => {
  console.log('🖨️ [UPDATED-V2] Starting print for procedure:', procedureId);
  
  try {
    const procedure = dashboardData?.recentProcedures?.find(p => p.id === procedureId);
    if (!procedure) {
      console.error('Procedure not found for printing');
      toast({
        title: "Error",
        description: "Procedure not found for printing",
        variant: "destructive",
      });
      return;
    }

    console.log('🖨️ Procedure data:', procedure);
    
    // Fetch the full procedure details to get Global Procedure fields
    let procedureContent = '';
    try {
      const assignmentResponse = await practiceApi.getProcedureAssignment(procedureId);
      const proc = assignmentResponse.data?.procedure;
      console.log('🖨️ Full procedure from API:', proc);
      
      // Check if this is a Global Procedure
      const isGlobalProcedure = proc?.purpose || proc?.first24Hours || proc?.painAndSwelling || proc?.oralHygiene;
      
      if (isGlobalProcedure) {
        console.log('🖨️ Building content from Global Procedure fields');
        let parts = [];
        
        if (proc.purpose) {
          parts.push(`<strong>Purpose:</strong><br>${proc.purpose}`);
        }
        
        if (proc.first24Hours?.length > 0) {
          const items = Array.isArray(proc.first24Hours) ? proc.first24Hours : [proc.first24Hours];
          parts.push(`<br><strong>First 24 Hours:</strong><br>${items.filter(i => i).map(item => `• ${item}`).join('<br>')}`);
        }
        
        if (proc.painAndSwelling?.length > 0) {
          const items = Array.isArray(proc.painAndSwelling) ? proc.painAndSwelling : [proc.painAndSwelling];
          parts.push(`<br><strong>Pain and Swelling:</strong><br>${items.filter(i => i).map(item => `• ${item}`).join('<br>')}`);
        }
        
        if (proc.oralHygiene?.length > 0) {
          const items = Array.isArray(proc.oralHygiene) ? proc.oralHygiene : [proc.oralHygiene];
          parts.push(`<br><strong>Oral Hygiene:</strong><br>${items.filter(i => i).map(item => `• ${item}`).join('<br>')}`);
        }
        
        if (proc.diet?.length > 0) {
          const items = Array.isArray(proc.diet) ? proc.diet : [proc.diet];
          parts.push(`<br><strong>Diet:</strong><br>${items.filter(i => i).map(item => `• ${item}`).join('<br>')}`);
        }
        
        if (proc.activity?.length > 0) {
          const items = Array.isArray(proc.activity) ? proc.activity : [proc.activity];
          parts.push(`<br><strong>Activity:</strong><br>${items.filter(i => i).map(item => `• ${item}`).join('<br>')}`);
        }
        
        if (proc.specialPrecautions?.length > 0) {
          const items = Array.isArray(proc.specialPrecautions) ? proc.specialPrecautions : [proc.specialPrecautions];
          parts.push(`<br><strong>Special Precautions:</strong><br>${items.filter(i => i).map(item => `• ${item}`).join('<br>')}`);
        }
        
        if (proc.followUp?.length > 0) {
          const items = Array.isArray(proc.followUp) ? proc.followUp : [proc.followUp];
          parts.push(`<br><strong>Follow Up:</strong><br>${items.filter(i => i).map(item => `• ${item}`).join('<br>')}`);
        }
        
        procedureContent = parts.join('<br>') || 'No content available';
      } else {
        // Legacy format
        procedureContent = proc?.overview || proc?.fullText || 'No content available';
      }
    } catch (e) {
      console.error('Error fetching procedure details:', e);
      procedureContent = 'Content not available';
    }

    // Get patient info
    const patient = dashboardData?.recentPatients?.find(p => p.id === procedure.patientId);
    const patientName = patient ? `${patient.firstName} ${patient.lastName}` : procedure.patientName || 'Unknown Patient';
    
    // Use simple HTML print
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast({
        title: "Popup Blocked",
        description: "Please allow popups to print.",
        variant: "destructive",
      });
      return;
    }

    const practiceName = practice?.name || 'Dental Practice';
    const practicePhone = practice?.phone || '';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${procedure.procedureName} - ${patientName}</title>
        <style>
          * { box-sizing: border-box !important; margin: 0 !important; padding: 0 !important; }
          body {
            font-family: Arial, sans-serif !important;
            font-size: 12pt !important;
            line-height: 1.55 !important;
            margin: 0.6in !important;
            color: #1f2937 !important;
          }
          .header {
            text-align: center !important;
            margin-bottom: 18px !important;
            border-bottom: 2px solid #0b2545 !important;
            padding-bottom: 12px !important;
          }
          .header h1 {
            font-size: 18pt !important; font-weight: bold !important;
            color: #0b2545 !important; margin: 0 0 6px 0 !important;
          }
          .header p {
            font-size: 11pt !important; margin: 3px 0 !important;
            color: #475569 !important;
          }
          .procedure-title {
            font-size: 18pt !important; font-weight: bold !important;
            color: #0b2545 !important; text-align: center !important;
            margin: 18px auto 14px !important; line-height: 1.2 !important;
          }
          .patient-info {
            background: #f0f7ff !important; border-left: 4px solid #2563eb !important;
            padding: 10px 14px !important; margin: 14px 0 !important;
            font-size: 10pt !important; border-radius: 4px !important;
            color: #1f2937 !important;
          }
          .patient-info strong { font-size: 11pt !important; color: #0b2545 !important; }
          .content {
            font-size: 12pt !important; line-height: 1.55 !important;
            margin-top: 6px !important; color: #1f2937 !important;
          }
          .content strong {
            display: block !important; font-size: 13pt !important;
            font-weight: bold !important; color: #2563eb !important;
            margin-top: 16px !important; margin-bottom: 4px !important;
            padding-bottom: 4px !important;
            border-bottom: 1px solid #dbeafe !important;
          }
          .footer {
            margin-top: 28px !important; padding-top: 10px !important;
            border-top: 1px solid #cbd5e1 !important;
            font-size: 9pt !important; color: #64748b !important;
            text-align: center !important;
          }
          .footer p { margin: 2px 0 !important; }
          @media print {
            body { margin: 0.4in !important; }
            @page { margin: 0.4in !important; size: letter !important; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${practiceName}</h1>
          <p>${practicePhone}</p>
        </div>
        
        <div class="procedure-title">${procedure.procedureName}</div>
        
        <div class="patient-info">
          <strong>Patient: ${patientName}</strong>
          ${procedure.performedDate ? `<br>Date: ${new Date(procedure.performedDate).toLocaleDateString()}` : ''}
        </div>

        <div class="content">${procedureContent}</div>

        <div class="footer">
          <p>${practiceName}${practicePhone ? ' | ' + practicePhone : ''}</p>
          <p>Printed: ${new Date().toLocaleDateString()}</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Modern Chrome no longer reliably fires onload for document.write windows
    const triggerPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => { try { printWindow.close(); } catch {} }, 500);
        console.log('🖨️ Print complete');
      } catch (e) {
        console.error('❌ Print trigger error:', e);
      }
    };
    // Single guarded trigger. 300ms delay lets Chrome parse the inline
    // <style> before print() fires (otherwise the dialog can open against
    // an unstyled DOM). The guard prevents the duplicate "ghost window"
    // flash that happened when both onload + setTimeout fired.
    let _printed = false;
    const guardedPrint = () => { if (_printed) return; _printed = true; triggerPrint(); };
    setTimeout(guardedPrint, 300);
    
  } catch (error) {
    console.error('❌ Print error:', error);
    toast({
      title: "Error",
      description: "Failed to prepare document for printing",
      variant: "destructive",
    });
  }
};

export const printProblemSheet = async (problemData, { practice, toast }) => {
  try {
    // Create print window
    const printWindow = window.open('', '', 'width=800,height=600');
    
    printWindow.document.write('<html><head><title>Print Problem/Emergency</title>');
    printWindow.document.write('<style>');
    printWindow.document.write('@page { margin: 0.75in; }');
    printWindow.document.write('body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.6; margin: 0; }');
    printWindow.document.write('.header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; page-break-after: avoid; break-after: avoid; }');
    printWindow.document.write('.practice-name { font-size: 18px; font-weight: bold; margin-bottom: 5px; }');
    printWindow.document.write('.form-title { font-size: 20px; font-weight: bold; margin: 20px 0 10px 0; text-align: center; }');
    printWindow.document.write('h2 { font-weight: bold; color: #111827; font-size: 24px; text-align: center; margin-bottom: 8px; page-break-after: avoid; break-after: avoid; }');
    printWindow.document.write('h3 { font-weight: bold; color: #111827; margin-top: 24px; margin-bottom: 12px; font-size: 18px; page-break-after: avoid; break-after: avoid; }');
    printWindow.document.write('h4 { font-weight: bold; color: #111827; margin-top: 16px; margin-bottom: 8px; page-break-after: avoid; break-after: avoid; }');
    printWindow.document.write('p { orphans: 4; widows: 4; page-break-inside: avoid; break-inside: avoid; margin-bottom: 10px; line-height: 1.6; }');
    printWindow.document.write('.bullet-item { margin-left: 24px; margin-bottom: 6px; padding-left: 20px; text-indent: -20px; page-break-inside: avoid; break-inside: avoid; line-height: 1.5; }');
    printWindow.document.write('.footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; font-size: 10px; color: #666; page-break-inside: avoid; break-inside: avoid; }');
    printWindow.document.write('.footer-title { font-weight: bold; font-size: 12px; margin-bottom: 5px; }');
    printWindow.document.write('</style>');
    printWindow.document.write('</head><body>');
    
    // Header with practice info
    if (practice) {
      printWindow.document.write('<div class="header">');
      printWindow.document.write('<div class="practice-name">' + practice.name + '</div>');
      if (practice.address) {
        printWindow.document.write('<div style="font-size: 12px; color: #666;">' + practice.address + '</div>');
      }
      if (practice.phone) {
        printWindow.document.write('<div style="font-size: 12px; color: #666;">Phone: ' + practice.phone + '</div>');
      }
      printWindow.document.write('</div>');
    }
    
    // Content
    printWindow.document.write('<div style="padding: 0 20px;">');
    
    // Format content using same logic as email
    const rawLines = problemData.overview.split('\n');
    let isFirstLines = true;
    let titleCount = 0;
    let i = 0;
    
    while (i < rawLines.length) {
      const line = rawLines[i];
      const trimmed = line.trim();
      
      if (!trimmed) {
        printWindow.document.write('<div style="margin-bottom: 8px;"></div>');
        i++;
        continue;
      }
      
      // First 2 non-empty lines are title
      if (isFirstLines && titleCount < 2 && !trimmed.startsWith('This handout')) {
        printWindow.document.write('<h2>' + trimmed + '</h2>');
        titleCount++;
        i++;
        continue;
      }
      
      isFirstLines = false;
      
      // Numbered items
      if (trimmed && trimmed[0].match(/\d/) && trimmed.indexOf('.') < 4) {
        printWindow.document.write('<h4>' + trimmed + '</h4>');
        i++;
        continue;
      }
      
      // Section headings
      const isMainHeading = 
        trimmed.indexOf('Common Problems') >= 0 ||
        trimmed.indexOf('When to Call') >= 0 ||
        trimmed.indexOf('Warning Signs') >= 0 ||
        trimmed.indexOf('What You May') >= 0;
      
      if (isMainHeading) {
        printWindow.document.write('<h3>' + trimmed + '</h3>');
        i++;
        continue;
      }
      
      // Bullet points
      if (line.startsWith(' ') || line.startsWith('\t')) {
        const bulletText = trimmed.replace(/^[•-]\s*/, '');
        printWindow.document.write('<div class="bullet-item">• ' + bulletText + '</div>');
        i++;
        continue;
      }
      
      // Regular paragraphs
      let paragraph = trimmed;
      let j = i + 1;
      while (j < rawLines.length) {
        const nextLine = rawLines[j];
        const nextTrimmed = nextLine.trim();
        
        if (!nextTrimmed ||
            (nextTrimmed && nextTrimmed[0].match(/\d/) && nextTrimmed.indexOf('.') < 4) ||
            nextLine.startsWith(' ') ||
            nextLine.startsWith('\t') ||
            nextTrimmed.indexOf('Common Problems') >= 0 ||
            nextTrimmed.indexOf('When to Call') >= 0 ||
            nextTrimmed.indexOf('Warning Signs') >= 0 ||
            nextTrimmed.indexOf('What You May') >= 0) {
          break;
        }
        
        paragraph += ' ' + nextTrimmed;
        j++;
      }
      
      printWindow.document.write('<p>' + paragraph + '</p>');
      i = j;
    }
    
    printWindow.document.write('</div>');
    
    // Footer
    if (practice) {
      printWindow.document.write('<div class="footer">');
      printWindow.document.write('<div class="footer-title">' + practice.name + '</div>');
      if (practice.phone) {
        printWindow.document.write('<div>For questions, call: ' + practice.phone + '</div>');
      }
      printWindow.document.write('</div>');
    }
    
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    
    const triggerPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => { try { printWindow.close(); } catch {} }, 500);
      } catch (e) {
        console.error('Print trigger error:', e);
      }
    };
    // Single guarded trigger. 300ms delay lets Chrome parse the inline
    // <style> before print() fires (otherwise the dialog can open against
    // an unstyled DOM). The guard prevents the duplicate "ghost window"
    // flash that happened when both onload + setTimeout fired.
    let _printed = false;
    const guardedPrint = () => { if (_printed) return; _printed = true; triggerPrint(); };
    setTimeout(guardedPrint, 300);
    
  } catch (error) {
    console.error('Print error:', error);
    toast({
      title: "Print Failed",
      description: "Failed to print problem/emergency",
      variant: "destructive"
    });
  }
};

/**
 * Build & print a thorough Compliance Report for the selected patient.
 * Uses a hidden iframe (NOT window.open) so popup blockers can't kill it.
 * Total report includes:
 *   1. Practice header (logo, name, address, phone, email)
 *   2. Patient demographics card (name, DOB, age, email, phone, language, address)
 *   3. Summary statistics (total / signed / unsigned counts)
 *   4. Detailed per-form table (form, type, provider, assigned, signed, channel, status)
 *   5. Per-form audit blocks (signing IP, signed timezone, signature delivery method)
 *   6. Footer with audit trail and signing officer field
 */
export const printComplianceReport = (complianceForms, { patient, practice, toast }) => {
  if (!complianceForms || complianceForms.length === 0) {
    toast({
      title: 'No compliance forms',
      description: 'This patient has no compliance forms on file yet.',
      variant: 'destructive',
    });
    return;
  }

  const patientName =
    [patient.firstName, patient.lastName].filter(Boolean).join(' ') || 'Patient';
  const dob = patient.dateOfBirth
    ? new Date(patient.dateOfBirth).toLocaleDateString('en-US')
    : '—';
  let age = '—';
  if (patient.dateOfBirth) {
    const dobDate = new Date(patient.dateOfBirth);
    const diff = Date.now() - dobDate.getTime();
    const ageDate = new Date(diff);
    age = `${Math.abs(ageDate.getUTCFullYear() - 1970)}`;
  }

  // ---- Build report (always rendered in English regardless of browser locale) ----
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const reportTime = new Date().toLocaleTimeString('en-US');
  const practiceName = practice?.name || 'Dental Practice';

  // practice.address may be a string OR an object {street,city,state,zip}
  const addrSrc = practice?.address;
  let practiceAddress = '';
  if (typeof addrSrc === 'string') {
    practiceAddress = addrSrc;
  } else if (addrSrc && typeof addrSrc === 'object') {
    practiceAddress = [addrSrc.street, addrSrc.city, addrSrc.state, addrSrc.zip]
      .filter(Boolean)
      .join(', ');
  }
  const practicePhone = practice?.phone || '';
  const practiceEmail = practice?.email || practice?.notificationEmail || '';
  const logoUrl = practice?.branding?.logo || '';

  // Friendly language label (preferredLanguage stores ISO code like "es", "ko")
  const LANG_LABELS = {
    en: 'English', es: 'Spanish', fr: 'French', de: 'German', it: 'Italian',
    pt: 'Portuguese', ru: 'Russian', zh: 'Chinese', ja: 'Japanese', ko: 'Korean',
    vi: 'Vietnamese', ar: 'Arabic', hi: 'Hindi', pl: 'Polish', nl: 'Dutch',
  };
  const langCode = (patient.preferredLanguage || 'en').toLowerCase();
  const languageLabel = LANG_LABELS[langCode] || patient.preferredLanguage || 'English';

  // Sort: unsigned first (so they're visible at the top), then by date desc
  const sorted = [...complianceForms].sort((a, b) => {
    const aSigned = a.status === 'signed' || a.status === 'signed_in_office';
    const bSigned = b.status === 'signed' || b.status === 'signed_in_office';
    if (aSigned !== bSigned) return aSigned ? 1 : -1;
    return new Date(b.assignedDate || 0) - new Date(a.assignedDate || 0);
  });

  const total = sorted.length;
  const signedCount = sorted.filter(
    (c) => c.status === 'signed' || c.status === 'signed_in_office'
  ).length;
  const unsignedCount = total - signedCount;

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US') : '—');
  const fmtDateTime = (d) =>
    d
      ? `${new Date(d).toLocaleDateString('en-US')} ${new Date(d).toLocaleTimeString('en-US')}`
      : '—';

  const formType = (c) =>
    c.isPerioRefusalForm
      ? 'Periodontal Refusal of Treatment'
      : c.isXrayRefusalForm
      ? 'X-Ray Refusal Acknowledgment'
      : c.isFailureToCompleteForm
      ? 'Failure to Complete Treatment'
      : c.isPatientComplianceForm
      ? 'Patient Compliance Form'
      : c.isRefusalForm
      ? 'Patient Treatment Refusal'
      : 'Compliance Form';

  const summaryRows = sorted
    .map((c, idx) => {
      const isSigned = c.status === 'signed' || c.status === 'signed_in_office';
      const statusBadge = isSigned
        ? `<span class="badge badge-signed">SIGNED${c.status === 'signed_in_office' ? ' (IN-OFFICE)' : ''}</span>`
        : `<span class="badge badge-unsigned">UNSIGNED</span>`;
      const detail =
        c.isRefusalForm && c.treatmentRefused
          ? `Treatment refused: <strong>${c.treatmentRefused}</strong>`
          : c.isXrayRefusalForm
          ? 'Diagnostic X-Ray refusal'
          : c.isPerioRefusalForm
          ? 'Periodontal treatment refusal'
          : c.isFailureToCompleteForm
          ? 'Notice of incomplete treatment'
          : c.isPatientComplianceForm
          ? 'Patient compliance form'
          : '';
      return `
        <tr class="${isSigned ? '' : 'row-unsigned'}">
          <td>${idx + 1}</td>
          <td><strong>${c.consentName || '—'}</strong><br><small>${formType(c)}</small></td>
          <td>${c.dentistName || '—'}</td>
          <td>${fmtDate(c.assignedDate)}</td>
          <td>${isSigned ? fmtDate(c.signedAt) : '—'}</td>
          <td>${detail || '—'}</td>
          <td>${statusBadge}</td>
        </tr>`;
    })
    .join('');

  // Per-form detailed audit blocks
  const detailBlocks = sorted
    .map((c, idx) => {
      const isSigned = c.status === 'signed' || c.status === 'signed_in_office';
      return `
        <div class="form-block ${isSigned ? '' : 'block-unsigned'}">
          <div class="form-block-header">
            <span class="form-num">#${idx + 1}</span>
            <h3>${c.consentName || 'Compliance Form'}</h3>
            ${
              isSigned
                ? `<span class="badge badge-signed">SIGNED${c.status === 'signed_in_office' ? ' IN-OFFICE' : ''}</span>`
                : `<span class="badge badge-unsigned">UNSIGNED</span>`
            }
          </div>
          <table class="kv">
            <tr><th>Form Type</th><td>${formType(c)}</td></tr>
            <tr><th>Provider</th><td>${c.dentistName || '—'}</td></tr>
            <tr><th>Assigned Date</th><td>${fmtDateTime(c.assignedDate)}</td></tr>
            ${
              c.isRefusalForm && c.treatmentRefused
                ? `<tr><th>Treatment Refused</th><td><strong>${c.treatmentRefused}</strong></td></tr>`
                : ''
            }
            ${
              isSigned
                ? `<tr><th>Signed At</th><td>${fmtDateTime(c.signedAt)}</td></tr>
                   ${c.signedAtTimezone ? `<tr><th>Timezone</th><td>${c.signedAtTimezone}</td></tr>` : ''}
                   ${c.signedIpAddress ? `<tr><th>Signed IP Address</th><td>${c.signedIpAddress}</td></tr>` : ''}
                   ${c.deliveryMethod ? `<tr><th>Delivery</th><td>${c.deliveryMethod}</td></tr>` : ''}
                   ${c.signedPdfUrl ? `<tr><th>Signed PDF</th><td><span class="muted">on file</span></td></tr>` : ''}`
                : `<tr><th>Status</th><td><strong style="color:#b91c1c">Awaiting patient signature</strong></td></tr>
                   ${c.unsignedAlertSentAt ? `<tr><th>Unsigned Alert Sent</th><td>${fmtDateTime(c.unsignedAlertSentAt)}</td></tr>` : ''}`
            }
            ${c.practiceNotes ? `<tr><th>Notes</th><td>${c.practiceNotes}</td></tr>` : ''}
          </table>
        </div>`;
    })
    .join('');

  const html = `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<title>Compliance Report — ${patientName}</title>
<style>
* { box-sizing: border-box; }
body { font-family: -apple-system, "Segoe UI", Arial, sans-serif; color: #1f2937; padding: 28px; margin: 0; }
.header { display: flex; align-items: center; gap: 18px; border-bottom: 4px solid #0586ba; padding-bottom: 16px; margin-bottom: 22px; }
.header img { max-height: 70px; max-width: 180px; }
.header-text h1 { font-size: 24px; margin: 0; color: #0586ba; }
.header-text p { font-size: 12px; color: #555; margin: 2px 0; }
.report-title { background: #0586ba; color: #fff; padding: 10px 14px; border-radius: 6px; margin-bottom: 18px; }
.report-title h2 { margin: 0; font-size: 18px; }
.report-title p { margin: 2px 0 0; font-size: 12px; opacity: 0.92; }

.patient-card { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px; padding: 14px 18px; margin-bottom: 18px; }
.patient-card h3 { margin: 0 0 8px; color: #0586ba; font-size: 14px; text-transform: uppercase; letter-spacing: 0.05em; }
.patient-card .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px 18px; font-size: 13px; }
.patient-card .label { color: #6b7280; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
.patient-card .value { color: #111827; font-weight: 500; }

.stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 22px; }
.stat { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; text-align: center; }
.stat-num { font-size: 28px; font-weight: 700; }
.stat-label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.06em; margin-top: 2px; }
.stat.total { border-color: #0586ba; }
.stat.total .stat-num { color: #0586ba; }
.stat.signed { border-color: #16a34a; background: #f0fdf4; }
.stat.signed .stat-num { color: #16a34a; }
.stat.unsigned { border-color: #dc2626; background: #fef2f2; }
.stat.unsigned .stat-num { color: #dc2626; }

h2.section { font-size: 14px; color: #0586ba; text-transform: uppercase; letter-spacing: 0.06em; margin: 26px 0 10px; padding-bottom: 4px; border-bottom: 2px solid #0586ba; }

table.summary { width: 100%; border-collapse: collapse; font-size: 12px; }
table.summary th { background: #0586ba; color: #fff; text-align: left; padding: 8px; font-size: 11px; text-transform: uppercase; }
table.summary td { padding: 8px; border: 1px solid #e5e7eb; vertical-align: top; }
table.summary tr:nth-child(even) td { background: #f9fafb; }
table.summary tr.row-unsigned td { background: #fef2f2 !important; }

.badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; letter-spacing: 0.05em; }
.badge-signed { background: #dcfce7; color: #14532d; border: 1px solid #16a34a; }
.badge-unsigned { background: #dc2626; color: #fff; border: 1px solid #991b1b; }

.form-block { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; margin-bottom: 12px; page-break-inside: avoid; }
.form-block.block-unsigned { border-color: #dc2626; background: #fef2f2; }
.form-block-header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb; }
.form-block-header h3 { margin: 0; font-size: 14px; flex: 1; }
.form-num { background: #0586ba; color: #fff; width: 24px; height: 24px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; }
table.kv { width: 100%; border-collapse: collapse; font-size: 12px; }
table.kv th { text-align: left; color: #6b7280; font-weight: 600; padding: 4px 8px; width: 160px; vertical-align: top; }
table.kv td { padding: 4px 8px; vertical-align: top; }

.footer { margin-top: 28px; padding-top: 14px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; }
.signature-line { margin-top: 28px; display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
.sig-block { border-top: 1px solid #1f2937; padding-top: 4px; font-size: 11px; color: #6b7280; }

.muted { color: #9ca3af; }

@media print {
  body { padding: 0; }
  .form-block { page-break-inside: avoid; }
  h2.section { page-break-after: avoid; }
}
</style>
</head><body>
<div class="header">
  ${logoUrl ? `<img src="${logoUrl}" alt="${practiceName}">` : ''}
  <div class="header-text">
    <h1>${practiceName}</h1>
    ${practiceAddress ? `<p>${practiceAddress}</p>` : ''}
    ${practicePhone ? `<p>Phone: ${practicePhone}${practiceEmail ? ` &nbsp;·&nbsp; ${practiceEmail}` : ''}</p>` : ''}
  </div>
</div>

<div class="report-title">
  <h2>Compliance Forms — Audit Report</h2>
  <p>Patient-specific compliance and refusal documentation summary</p>
</div>

<div class="patient-card">
  <h3>Patient Information</h3>
  <div class="grid">
    <div><div class="label">Name</div><div class="value">${patientName}</div></div>
    <div><div class="label">Date of Birth</div><div class="value">${dob}${age !== '—' ? ` (age ${age})` : ''}</div></div>
    <div><div class="label">Patient ID</div><div class="value">${(patient.id || '').slice(0, 8) || '—'}</div></div>
    <div><div class="label">Email</div><div class="value">${patient.email || '—'}</div></div>
    <div><div class="label">Phone</div><div class="value">${patient.phone || patient.cellPhone || '—'}</div></div>
    <div><div class="label">Preferred Language</div><div class="value">${languageLabel}</div></div>
  </div>
</div>

<div class="stats">
  <div class="stat total">
    <div class="stat-num">${total}</div>
    <div class="stat-label">Total Forms</div>
  </div>
  <div class="stat signed">
    <div class="stat-num">${signedCount}</div>
    <div class="stat-label">Signed</div>
  </div>
  <div class="stat unsigned">
    <div class="stat-num">${unsignedCount}</div>
    <div class="stat-label">Unsigned</div>
  </div>
</div>

<h2 class="section">Summary</h2>
<table class="summary">
  <thead>
    <tr><th>#</th><th>Form</th><th>Provider</th><th>Assigned</th><th>Signed</th><th>Detail</th><th>Status</th></tr>
  </thead>
  <tbody>${summaryRows}</tbody>
</table>

<h2 class="section">Detailed Audit Records</h2>
${detailBlocks}

<div class="footer">
  <p><strong>Report Generated:</strong> ${today} at ${reportTime} &nbsp;·&nbsp; ${total} compliance form${total === 1 ? '' : 's'} on file</p>
  <p>This report is generated automatically by PractCom and reflects the current state of the patient's compliance record.</p>
  <div class="signature-line">
    <div class="sig-block">Reviewed by (print name &amp; sign)</div>
    <div class="sig-block">Date</div>
  </div>
</div>

<script>
  // Auto-trigger print as soon as everything renders.
  window.addEventListener('load', function () {
    setTimeout(function () { try { window.print(); } catch (e) {} }, 400);
  });
</script>
</body></html>`;

  // ---- Bypass popup blockers via blob URL + hidden iframe ----
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);

  // Remove any prior compliance-print iframe so we don't accumulate them
  const existing = document.getElementById('compliance-print-frame');
  if (existing) existing.remove();

  const iframe = document.createElement('iframe');
  iframe.id = 'compliance-print-frame';
  iframe.src = url;
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.setAttribute('aria-hidden', 'true');

  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (err) {
      console.error('Compliance print error:', err);
    }
    // Cleanup ~30s later (give browser time to actually print)
    setTimeout(() => {
      URL.revokeObjectURL(url);
      iframe.remove();
    }, 30000);
  };

  document.body.appendChild(iframe);

  toast({
    title: 'Compliance report ready',
    description: `${total} form${total === 1 ? '' : 's'} for ${patientName} — print dialog opening…`,
  });
};

/**
 * Generic patient-tab report builder. Generates a print-friendly report for
 * any tab (Procedures, Informed Consents, Med Cards, Referrals, Follow-Ups…).
 * Each tab passes a config that defines its title, columns, and per-row data.
 *
 * config = {
 *   tabKey: 'procedures',         // used in iframe id + report title
 *   reportTitle: 'Procedures Report',
 *   subtitle:    'Patient procedure history & status',
 *   itemNoun:    'procedure',     // singular noun for stats
 *   items:       [...],           // raw data array
 *   columns:     [{key,label}, ...],
 *   rowFor:      (item) => [...], // returns array of html-safe cell strings, same length as columns
 *   stats:       (items) => [{label,value,color}, ...] // optional summary cards
 * }
 */
export const printPatientReport = (config, { patient, practice, toast }) => {
  const {
    tabKey,
    reportTitle,
    subtitle = '',
    itemNoun = 'item',
    items = [],
    columns = [],
    rowFor,
    stats: statsFn,
    headerColor = '#0586ba',
  } = config;

  if (!items || items.length === 0) {
    toast({
      title: `No ${itemNoun}s to print`,
      description: `This patient has no ${itemNoun}s on file yet.`,
      variant: 'destructive',
    });
    return;
  }

  const patientName =
    [patient.firstName, patient.lastName].filter(Boolean).join(' ') || 'Patient';
  const dob = patient.dateOfBirth
    ? new Date(patient.dateOfBirth).toLocaleDateString('en-US')
    : '—';
  let age = '—';
  if (patient.dateOfBirth) {
    const dobDate = new Date(patient.dateOfBirth);
    const diff = Date.now() - dobDate.getTime();
    const ageDate = new Date(diff);
    age = `${Math.abs(ageDate.getUTCFullYear() - 1970)}`;
  }

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const reportTime = new Date().toLocaleTimeString('en-US');
  const practiceName = practice?.name || 'Dental Practice';

  const addrSrc = practice?.address;
  let practiceAddress = '';
  if (typeof addrSrc === 'string') practiceAddress = addrSrc;
  else if (addrSrc && typeof addrSrc === 'object') {
    practiceAddress = [addrSrc.street, addrSrc.city, addrSrc.state, addrSrc.zip]
      .filter(Boolean).join(', ');
  }
  const practicePhone = practice?.phone || '';
  const practiceEmail = practice?.email || practice?.notificationEmail || '';
  const logoUrl = practice?.branding?.logo || '';

  const LANG_LABELS = { en:'English', es:'Spanish', fr:'French', de:'German', it:'Italian', pt:'Portuguese', ru:'Russian', zh:'Chinese', ja:'Japanese', ko:'Korean', vi:'Vietnamese', ar:'Arabic', hi:'Hindi', pl:'Polish', nl:'Dutch' };
  const langCode = (patient.preferredLanguage || 'en').toLowerCase();
  const languageLabel = LANG_LABELS[langCode] || patient.preferredLanguage || 'English';

  // Build summary table rows from caller's rowFor()
  const tableRows = items.map((item, idx) => {
    const cells = rowFor(item, idx) || [];
    return `<tr>
      <td>${idx + 1}</td>
      ${cells.map((c) => `<td>${c == null || c === '' ? '—' : c}</td>`).join('')}
    </tr>`;
  }).join('');

  const tableHead = `<tr><th>#</th>${columns.map((c) => `<th>${c.label}</th>`).join('')}</tr>`;

  // Optional stat cards
  const statsArr = (typeof statsFn === 'function') ? (statsFn(items) || []) : [];
  const statsHtml = statsArr.length > 0 ? `
    <div class="stats">
      ${statsArr.map((s) => `
        <div class="stat" style="border-color:${s.color || headerColor}">
          <div class="stat-num" style="color:${s.color || headerColor}">${s.value}</div>
          <div class="stat-label">${s.label}</div>
        </div>`).join('')}
    </div>` : '';

  const total = items.length;

  const html = `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<title>${reportTitle} — ${patientName}</title>
<style>
* { box-sizing: border-box; }
body { font-family: -apple-system, "Segoe UI", Arial, sans-serif; color: #1f2937; padding: 28px; margin: 0; }
.header { display: flex; align-items: center; gap: 18px; border-bottom: 4px solid ${headerColor}; padding-bottom: 16px; margin-bottom: 22px; }
.header img { max-height: 70px; max-width: 180px; }
.header-text h1 { font-size: 24px; margin: 0; color: ${headerColor}; }
.header-text p { font-size: 12px; color: #555; margin: 2px 0; }
.report-title { background: ${headerColor}; color: #fff; padding: 10px 14px; border-radius: 6px; margin-bottom: 18px; }
.report-title h2 { margin: 0; font-size: 18px; }
.report-title p { margin: 2px 0 0; font-size: 12px; opacity: 0.92; }

.patient-card { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px; padding: 14px 18px; margin-bottom: 18px; }
.patient-card h3 { margin: 0 0 8px; color: ${headerColor}; font-size: 14px; text-transform: uppercase; letter-spacing: 0.05em; }
.patient-card .grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px 18px; font-size: 13px; }
.patient-card .label { color: #6b7280; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
.patient-card .value { color: #111827; font-weight: 500; }

.stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px; margin-bottom: 22px; }
.stat { background: #fff; border: 2px solid #e5e7eb; border-radius: 8px; padding: 14px; text-align: center; }
.stat-num { font-size: 28px; font-weight: 700; }
.stat-label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.06em; margin-top: 2px; }

table.summary { width: 100%; border-collapse: collapse; font-size: 12px; }
table.summary th { background: ${headerColor}; color: #fff; text-align: left; padding: 8px; font-size: 11px; text-transform: uppercase; }
table.summary td { padding: 8px; border: 1px solid #e5e7eb; vertical-align: top; }
table.summary tr:nth-child(even) td { background: #f9fafb; }

.badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; font-weight: 700; letter-spacing: 0.05em; }
.badge-green { background:#dcfce7; color:#14532d; border:1px solid #16a34a; }
.badge-red   { background:#dc2626; color:#fff;    border:1px solid #991b1b; }
.badge-amber { background:#fef3c7; color:#78350f; border:1px solid #d97706; }
.badge-gray  { background:#f3f4f6; color:#374151; border:1px solid #9ca3af; }

.footer { margin-top: 28px; padding-top: 14px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280; }
.signature-line { margin-top: 28px; display: grid; grid-template-columns: 1fr 1fr; gap: 28px; }
.sig-block { border-top: 1px solid #1f2937; padding-top: 4px; font-size: 11px; color: #6b7280; }

@media print { body { padding: 0; } table.summary { page-break-inside: auto; } }
</style>
</head><body>
<div class="header">
  ${logoUrl ? `<img src="${logoUrl}" alt="${practiceName}">` : ''}
  <div class="header-text">
    <h1>${practiceName}</h1>
    ${practiceAddress ? `<p>${practiceAddress}</p>` : ''}
    ${practicePhone ? `<p>Phone: ${practicePhone}${practiceEmail ? ` &nbsp;·&nbsp; ${practiceEmail}` : ''}</p>` : ''}
  </div>
</div>

<div class="report-title">
  <h2>${reportTitle}</h2>
  ${subtitle ? `<p>${subtitle}</p>` : ''}
</div>

<div class="patient-card">
  <h3>Patient Information</h3>
  <div class="grid">
    <div><div class="label">Name</div><div class="value">${patientName}</div></div>
    <div><div class="label">Date of Birth</div><div class="value">${dob}${age !== '—' ? ` (age ${age})` : ''}</div></div>
    <div><div class="label">Patient ID</div><div class="value">${(patient.id || '').slice(0, 8) || '—'}</div></div>
    <div><div class="label">Email</div><div class="value">${patient.email || '—'}</div></div>
    <div><div class="label">Phone</div><div class="value">${patient.phone || patient.cellPhone || '—'}</div></div>
    <div><div class="label">Preferred Language</div><div class="value">${languageLabel}</div></div>
  </div>
</div>

${statsHtml}

<table class="summary">
  <thead>${tableHead}</thead>
  <tbody>${tableRows}</tbody>
</table>

<div class="footer">
  <p><strong>Report Generated:</strong> ${today} at ${reportTime} &nbsp;·&nbsp; ${total} ${itemNoun}${total === 1 ? '' : 's'} on file</p>
  <p>This report is generated automatically by PractCom and reflects the current state of the patient's record.</p>
  <div class="signature-line">
    <div class="sig-block">Reviewed by (print name &amp; sign)</div>
    <div class="sig-block">Date</div>
  </div>
</div>

<script>window.addEventListener('load', function () { setTimeout(function () { try { window.print(); } catch (e) {} }, 400); });</script>
</body></html>`;

  // Print via hidden iframe (popup-blocker proof)
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const frameId = `${tabKey}-print-frame`;
  const existing = document.getElementById(frameId);
  if (existing) existing.remove();

  const iframe = document.createElement('iframe');
  iframe.id = frameId;
  iframe.src = url;
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.onload = () => {
    try { iframe.contentWindow?.focus(); iframe.contentWindow?.print(); } catch (err) { console.error(err); }
    setTimeout(() => { URL.revokeObjectURL(url); iframe.remove(); }, 30000);
  };
  document.body.appendChild(iframe);

  toast({
    title: `${reportTitle} ready`,
    description: `${total} ${itemNoun}${total === 1 ? '' : 's'} for ${patientName} — print dialog opening…`,
  });
};
